/**
 * PaymentSuccessPage — 決済完了ページ (/payment/success)
 *
 * Stripe決済後のリダイレクト先。
 * 1. URLから session_id を取得
 * 2. /checkout/verify を呼び出して決済確認 + JWT取得
 * 3. イベントのactiveEventIdを設定して /operator へ遷移
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionToken } from '../hooks/useSessionToken';
import { useEventStore } from '../stores/eventStore';
import { t, useLang } from '../i18n';

const WORKER_URL = import.meta.env.VITE_WORKER_URL as string ?? 'http://localhost:8787';

type VerifyState = 'verifying' | 'success' | 'error';

export default function PaymentSuccessPage() {
  useLang();
  const navigate = useNavigate();
  const { verifyCheckout } = useSessionToken();
  const { events, load, setActiveEvent } = useEventStore();
  const [state, setState] = useState<VerifyState>('verifying');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [dots, setDots] = useState('');
  const isExtension = new URLSearchParams(window.location.search).get('type') === 'extension';

  // アニメーションドット
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const isExtension = params.get('type') === 'extension';

    if (!sessionId) {
      setState('error');
      setErrorMsg(t('決済セッションIDが見つかりません。', 'Payment session ID not found.'));
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        // ストアを初期化
        load();

        // Webhook処理を待つ（最大10秒リトライ）
        let result: { sessionToken: string; sessionId: string; mode: string } | null = null;
        let lastError = '';

        for (let attempt = 0; attempt < 5; attempt++) {
          if (cancelled) return;
          if (attempt > 0) await new Promise((r) => setTimeout(r, 2000));

          try {
            const res = await fetch(`${WORKER_URL}/checkout/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ checkoutSessionId: sessionId }),
            });
            const data = (await res.json()) as { sessionToken?: string; sessionId?: string; mode?: string; error?: string };

            if (res.ok && data.sessionToken) {
              result = { sessionToken: data.sessionToken, sessionId: data.sessionId!, mode: data.mode! };
              break;
            }
            lastError = data.error ?? `HTTP ${res.status}`;
          } catch (e) {
            lastError = e instanceof Error ? e.message : t('通信エラー', 'Network error');
          }
        }

        if (cancelled) return;

        if (!result) {
          setState('error');
          setErrorMsg(t(`決済確認に失敗しました: ${lastError}`, `Could not confirm payment: ${lastError}`));
          return;
        }

        // セッションストレージにJWT情報を保存（OperatorPageが拾う）
        sessionStorage.setItem('andiamo_jwt', result.sessionToken);
        sessionStorage.setItem('andiamo_session_id', result.sessionId);
        sessionStorage.setItem('andiamo_checkout_session_id', sessionId);

        setState('success');

        // 延長決済の場合はタブを閉じる（元のオペレーターページはWSで自動更新）
        if (isExtension) {
          setTimeout(() => {
            if (!cancelled) {
              window.close();
              // window.close()が効かない場合（ブラウザが拒否）はメッセージ表示のみ
            }
          }, 1500);
          return;
        }

        // 通常決済: /operator へ遷移
        setTimeout(() => {
          if (!cancelled) {
            navigate('/operator?mode=production&from=payment', { replace: true });
          }
        }, 1500);

      } catch (e) {
        if (cancelled) return;
        setState('error');
        setErrorMsg(e instanceof Error ? e.message : t('不明なエラー', 'Unknown error'));
      }
    };

    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#111827',
      padding: '1rem',
    }}>
      <div style={{
        background: '#1f2937',
        borderRadius: 12,
        padding: '2.5rem 2rem',
        maxWidth: 420,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      }}>
        {state === 'verifying' && (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
            <h2 style={{ color: '#f9fafb', fontSize: '1.25rem', marginBottom: '0.5rem' }}>
              {t('決済を確認中', 'Confirming payment')}{dots}
            </h2>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              {t('しばらくお待ちください。', 'Please wait a moment.')}
            </p>
            <div style={{
              width: 48,
              height: 4,
              background: '#1d4ed8',
              borderRadius: 2,
              margin: '1.5rem auto 0',
              animation: 'pulse 1s infinite',
            }} />
          </>
        )}

        {state === 'success' && (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ color: '#10b981', fontSize: '1.25rem', marginBottom: '0.5rem' }}>
              {isExtension ? t('延長が完了しました', 'Extension complete') : t('決済が完了しました', 'Payment complete')}
            </h2>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              {isExtension
                ? t('このタブを閉じて、翻訳画面に戻ってください。', 'Close this tab and return to the operator screen.')
                : t(`翻訳画面へ移動します${dots}`, `Opening the operator screen${dots}`)}
            </p>
          </>
        )}

        {state === 'error' && (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <h2 style={{ color: '#f9fafb', fontSize: '1.25rem', marginBottom: '0.5rem' }}>
              {t('確認に時間がかかっています', 'Confirmation is taking longer than usual')}
            </h2>
            {errorMsg && (
              <p style={{
                color: '#ef4444',
                fontSize: '0.8rem',
                background: '#1f1515',
                padding: '0.75rem',
                borderRadius: 6,
                marginBottom: '1rem',
              }}>
                {errorMsg}
              </p>
            )}
            <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              {t('決済は完了している場合があります。', 'Your payment may already be complete.')}<br />
              {t('しばらく待ってからもう一度お試しください。', 'Please wait a moment and try again.')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: '#1d4ed8',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
              >
                {t('再確認する', 'Check again')}
              </button>
              <button
                onClick={() => navigate('/setup', { replace: true })}
                style={{
                  background: 'transparent',
                  color: '#9ca3af',
                  border: '1px solid #374151',
                  borderRadius: 6,
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
              >
                {t('セットアップに戻る', 'Back to setup')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
